import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { isDesktop } from '../../../../utils/mobileHelpers';

import Popup from '../../../common/Popup';
import Button from '../../../Button';
import SelectCustom from '../../../SelectCustom';
import MenuList from '../../../common/AsyncPaginatedSelect/components/MenuList';
import OptionLabel from '../../../common/AsyncPaginatedSelect/components/OptionLabel';

import { actions as organizationsActions } from '../../../../redux/payment-methods/requests/organizations-req';
import { actions as locationsActions } from '../../../../redux/payment-methods/requests/locations-req';
import {
  selectLocationList,
  selectOrganizationList,
  selectLocationListIsFetching,
} from '../../../../redux/payment-methods/selectors';
import { actions as accountVaultsFormActions } from '../../../../redux/programs/accountVaultsForm';
import { consts as popupConsts } from '../../../../redux/ui/popup';
import { UI_POPUP_TYPES } from '../../../../constants/props';
import { targetUserIdSelector } from '../../../../redux/context/selectors';
import { mapListItemToOption } from './utils/map-list-item-to-option';

import styles from './index.styl';

const OrganizationPopup = ({ opened, onClose }) => {
  const dispatch = useDispatch();

  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const [selectedLocation, setSelectedLocation] = useState(null);

  const { formatMessage } = useIntl();

  const organizationList = useSelector(selectOrganizationList);
  const locationList = useSelector(selectLocationList);
  const isLoading = useSelector(selectLocationListIsFetching);
  const userId = useSelector(targetUserIdSelector);

  const warningTitle = formatMessage({
    id: 'profile.payment.addingLocationInfo',
  });

  const organizationOptions = useMemo(() => {
    return organizationList.map(mapListItemToOption);
  }, [organizationList]);

  const locationOptions = useMemo(() => {
    return locationList.map(({ id, name, fullAddress }) => ({
      value: id,
      label: `${name}, ${fullAddress}`,
    }));
  }, [locationList]);

  const onClosePopup = () => {
    onClose();
    setSelectedOrganization(null);
    setSelectedLocation(null);
  };

  const handleSelectOrganization = option => {
    setSelectedOrganization(option.value);

    setSelectedLocation(null);

    dispatch(locationsActions.clear());

    dispatch(
      locationsActions.send({
        organizationId: option.value,
      }),
    );
  };

  const handleSelectLocation = option => {
    setSelectedLocation(option.value);
  };

  const handleLocationSearch = debounce(text => {
    dispatch(locationsActions.clear());

    dispatch(
      locationsActions.send({
        organizationId: selectedOrganization,
        query: text,
      }),
    );
  }, 700);

  const handleAddNewCard = () => {
    onClosePopup();
    dispatch(
      accountVaultsFormActions.send({
        userId,
        locationId: selectedLocation,
      }),
    );

    dispatch({
      type: popupConsts.SHOW,
      payload: {
        contentType: UI_POPUP_TYPES.PAYA_ACCOUNT_FORM,
        contentOptions: {
          noCards: false,
        },
      },
    });
  };

  useEffect(() => {
    dispatch(organizationsActions.clear());

    if (opened) {
      dispatch(organizationsActions.send());
    }
  }, [opened]);

  return (
    <Popup
      strictClose
      opened={opened}
      onClose={onClosePopup}
      width={isDesktop() ? 'small' : 'medium'}
      title={formatMessage({
        id: 'profile.payment.informationAboutTheOrganizationTitle',
      })}
    >
      <div className={styles.content}>
        <div className={styles.description}>
          {formatMessage({
            id: 'profile.payment.informationAboutTheOrganizationDescription',
          })}
        </div>

        <div className={styles.content}>
          <SelectCustom
            label={formatMessage({ id: 'profile.payment.chooseOrganization' })}
            placeholder={formatMessage({
              id: 'profile.payment.fields.organization',
            })}
            value={selectedOrganization}
            onChange={handleSelectOrganization}
            options={organizationOptions}
            disabled={!organizationList.length}
          />

          <SelectCustom
            disabled={!selectedOrganization}
            value={selectedLocation}
            onChange={handleSelectLocation}
            onInputChange={handleLocationSearch}
            label={formatMessage({ id: 'profile.payment.chooseLocation' })}
            placeholder={formatMessage({
              id: 'profile.payment.fields.location',
            })}
            options={locationOptions}
            components={{ MenuList, LoadingMessage: () => null }}
            filterOption={null}
            isLoading={isLoading}
            formatOptionLabel={OptionLabel}
            menuPosition="fixed"
          />
        </div>

        {selectedLocation && (
          <div className={styles.warning}>{warningTitle}</div>
        )}

        <div className={styles.buttonWrapper}>
          <Button
            color="primary"
            className={styles.button}
            disabled={!selectedLocation}
            onClick={handleAddNewCard}
          >
            {formatMessage({ id: 'common.next' })}
          </Button>

          <Button
            color="accent"
            className={styles.button}
            onClick={onClosePopup}
          >
            {formatMessage({ id: 'common.cancel' })}
          </Button>
        </div>
      </div>
    </Popup>
  );
};
export default OrganizationPopup;

OrganizationPopup.propTypes = {
  opened: PropTypes.bool,
  onClose: PropTypes.func,
};



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/ProfilePaymentMethods/OrganizationPopup/index.jsx